import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../../UI/Buttons';
import { Title, Label, Text } from '../../UI/Typography';
import { FormGroup, Input, Textarea } from '../../UI/Inputs';
import FileInput from '../../UI/FileInput';
import { registerCompany } from '../../../Services/Api/companyService';

const FormContainer = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.xs}; /* Changed from theme.spacing.lg to theme.spacing.xs */
  margin-bottom: ${({ theme }) => theme.spacing.xs}; /* Reduced from theme.spacing.lg */

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xs}; /* Even smaller gap on mobile */
  }
`;

const FullWidthFormGroup = styled(FormGroup)`
  grid-column: 1 / -1;
  margin-top: ${({ theme }) => theme.spacing.xs}; /* Add small top margin for separation */
`;

const SubmitButton = styled(Button)`
  margin-top: ${({ theme }) => theme.spacing.xs}; /* Reduced from theme.spacing.lg */
  width: 100%;
`;

const StatusMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  text-align: center;
  background-color: ${props => 
    props.type === 'success' 
      ? props.theme.colors.successLight 
      : props.theme.colors.errorLight
  };
  color: ${props => 
    props.type === 'success' 
      ? props.theme.colors.successDark 
      : props.theme.colors.errorDark
  };
  border: 1px solid ${props => 
    props.type === 'success' 
      ? props.theme.colors.success 
      : props.theme.colors.error
  };
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const CompanyRegisterForm = ({ onSuccessfulSubmit }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    company_type: '',
    company_registration_number: '',
    company_address: '',
    company_logo: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (file) => {
    setFormData(prev => ({
      ...prev,
      company_logo: file
    }));
  };

  const validateForm = () => {
    const required = ['company_name', 'company_type', 'company_registration_number', 'company_address'];
    
    for (const field of required) {
      if (!formData[field] || formData[field].trim() === '') {
        return `${field.replace('_', ' ')} is required`;
      }
    }
    
    if (!formData.company_logo) {
      return 'Company logo is required';
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(formData.company_logo.type)) {
      return 'Logo must be an image file (JPG, PNG, GIF)';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    const validationError = validateForm();
    if (validationError) {
      setSubmitStatus({ type: 'error', message: validationError });
      return;
    }

    setIsLoading(true);

    try {
      const result = await registerCompany(formData);
      setSubmitStatus({ 
        type: 'success', 
        message: 'Company registered successfully! You can now login.' 
      });

      // Reset form
      setFormData({
        company_name: '',
        company_type: '',
        company_registration_number: '',
        company_address: '',
        company_logo: null
      });

      if (onSuccessfulSubmit) {
        setTimeout(() => {
          onSuccessfulSubmit(result);
        }, 2000);
      }

    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: error.message || 'Failed to register company' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormContainer>
      <form onSubmit={handleSubmit}>
        <FormGrid>
          <FormGroup>
            <Label htmlFor="company_name">Company Name *</Label>
            <Input
              type="text"
              id="company_name"
              value={formData.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              placeholder="e.g., Qualqem Technologies"
              required
              disabled={isLoading}
              maxLength={100}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="company_type">Company Type *</Label>
            <Input
              type="text"
              id="company_type"
              value={formData.company_type}
              onChange={(e) => handleInputChange('company_type', e.target.value)}
              placeholder="e.g., (PVT) LTD, LLC, Corporation"
              required
              disabled={isLoading}
              maxLength={50}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="company_registration_number">Registration Number *</Label>
            <Input
              type="text"
              id="company_registration_number"
              value={formData.company_registration_number}
              onChange={(e) => handleInputChange('company_registration_number', e.target.value)}
              placeholder="e.g., reg-1234"
              required
              disabled={isLoading}
              maxLength={50}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="company_logo">Company Logo *</Label>
            <FileInput
              id="company_logo"
              accept="image/*"
              onChange={handleFileChange}
              disabled={isLoading}
              buttonText="Upload Logo"
              selectedFile={formData.company_logo}
              maxSizeInMB={5}
            />
          </FormGroup>
        </FormGrid>

        <FullWidthFormGroup>
          <Label htmlFor="company_address">Company Address *</Label>
          <Textarea
            id="company_address"
            value={formData.company_address}
            onChange={(e) => handleInputChange('company_address', e.target.value)}
            placeholder="e.g., 143/3, Park Road, Colombo 05"
            required
            disabled={isLoading}
            rows={3}
            maxLength={500}
          />
        </FullWidthFormGroup>

        {submitStatus && (
          <StatusMessage type={submitStatus.type}>
            {submitStatus.message}
          </StatusMessage>
        )}

        <SubmitButton 
          type="submit" 
          disabled={isLoading}
          variant="primary"
        >
          {isLoading ? 'Registering Company...' : 'Register Company'}
        </SubmitButton>
      </form>
    </FormContainer>
  );
};

export default CompanyRegisterForm;